import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity, EntityIdentifier, EntityObject, isEntity} from "./entity";
import {TimeGroup, toTimeGroup} from "./time-group";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {RegionEntity} from "./region";
import {Quantity} from "../types/types";
import {FreightTypeEntity} from "./freight-type";
import {Exportable} from "./exportable";
import {adjustFromTimeZone, format} from "../date/date";
import {Default} from "../util/decorators";

export const ProductionVolumeValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    timeGroup: t.string,
    dttm: t.number,
    quantity: t.number,
    unitOfMeasure: t.string,
    regionId: t.string,
  }),
  t.partial({
    generatedFromMeterReading: t.boolean,
    objectName: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ProductionVolumeArrayValidator = t.array(ProductionVolumeValidator);

export type ProductionVolume = t.TypeOf<typeof ProductionVolumeValidator>;
export type ProductionVolumeArray = t.TypeOf<typeof ProductionVolumeArrayValidator>;

@Exclude()
export class ProductionVolumeEntity implements Entity, Exportable {
  @Expose()
  id!: string;

  @Expose()
  objectName?: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Transform(getDatabaseCodeTransformer(toTimeGroup))
  @Expose()
  timeGroup!: TimeGroup;

  @Expose()
  dttm!: number;

  @Expose()
  quantity!: number;

  @Expose()
  freightTypeId!: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  unitOfMeasure!: string;

  @Expose()
  regionId!: string;

  @Expose()
  @Default(false)
  generatedFromMeterReading = false;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'PROD_VOL';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for ProductionVolumeEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ProductionVolume): ProductionVolumeEntity {
    return plainToInstance(ProductionVolumeEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ProductionVolume> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ProductionVolumeValidator, object);
  }

  asTypeValue(): ProductionVolume {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ProductionVolumeValidator, object);
  }

  setRegion(value: RegionEntity | string) {
    if (typeof value === 'string') {
      this.regionId = value;
    } else {
      this.regionId = value.id;
    }
    return this;
  }

  setObject(value: Entity | EntityObject) {
    if (isEntity(value)) {
      this.objectId = value.id;
      this.objectType = value.entityType;
      this.objectName = value.displayName;
    } else {
      this.objectId = value.objectId;
      this.objectType = value.objectType;
      this.objectName = value.objectName;
    }
    return this;
  }

  setQuantity(quantity: Quantity) {
    this.quantity = quantity.number;
    this.unitOfMeasure = quantity.units;
    return this;
  }

  setFreightType(value: FreightTypeEntity | string) {
    if (typeof value === 'string') {
      this.freightTypeId = value;
    } else {
      this.freightTypeId = value.id;
      this.freightTypeName = value.name;
    }
    return this;
  }

  readonly CSV_HEADERS: string[] = [
    'Date',
    'Time Group',
    'Object',
    'Object Type',
    'Quantity',
    'Unit Of Measure',
    'Freight Type',
    'Auto Generated',
  ];

  asCsv(): string[] {
    return [
      format(adjustFromTimeZone(this.dttm), 'yyyy-MM-dd HH:mm:ss'),
      this.timeGroup.code,
      this.objectName ?? '',
      this.objectType ?? '',
      this.quantity.toString(),
      this.unitOfMeasure,
      this.freightTypeName ?? '',
      this.generatedFromMeterReading.toString(),
    ];
  }
}

export const ProductionVolumeImportResultsValidator = t.intersection([
  t.type({
    pipelineFlows: t.array(ProductionVolumeValidator),
    missingMappings: t.array(t.string),
  }),
  t.partial({
    // partial props here
  }),
]);

export type ProductionVolumeImportResults = t.TypeOf<
  typeof ProductionVolumeImportResultsValidator
>;

export interface ProductionVolumeSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  freightTypeIds?: string[];
  categories?: string[];
  entityIds?: EntityIdentifier[];
  meterReadingCriteria?: {
    generate: boolean;
    timeGroup?: string;
    uom?: string;
  };
}
